import { MAX_PLAYERS, TICK_RATE } from "./constants.ts";
import { GameMode, type MatchRules } from "./match.ts";
import { isTeam, type TeamId } from "./teams.ts";

// ---------------------------------------------------------------------------
// Room codes (GDD §6.3): short, non-sequential, easy to read aloud.
// ---------------------------------------------------------------------------

/** No 0/O or 1/I: codes are read aloud and typed on phones. */
export const ROOM_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
const ROOM_CODE_PATTERN = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);

/** `randomInt(n)` must return a uniform integer in [0, n) — on the server, `crypto.randomInt`. */
export function generateRoomCode(randomInt: (max: number) => number): string {
  let code = "";
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) code += ROOM_CODE_ALPHABET[randomInt(ROOM_CODE_ALPHABET.length)];
  return code;
}

/** What a person typed → canonical code (case, spaces and dashes do not matter). */
export function normalizeRoomCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function isRoomCode(value: string): boolean {
  return ROOM_CODE_PATTERN.test(value);
}

/** WebSocket close codes with a meaning for the player (Colyseus uses 4000–4010). */
export const RoomCloseCode = {
  Kicked: 4100,
  Expired: 4101,
} as const;

// ---------------------------------------------------------------------------
// Room settings chosen by the host in the lobby.
// ---------------------------------------------------------------------------

export interface RoomSettings {
  /** Omitted in older presets = Slam. Replicated settings always contain it. */
  gameMode?: number;
  maxPlayers: number;
  /** First to this score wins. */
  targetScore: number;
  durationSec: number;
  /** 0 = free-for-all, 1 = two teams (D27–D29). */
  teams: number;
}

/** The only values a host may pick; anything else is refused by the server. */
export const SETTINGS_OPTIONS = {
  gameMode: [GameMode.Slam, GameMode.KO],
  maxPlayers: [2, 3, 4, 5, 6, 7, 8],
  targetScore: [5, 10, 15, 20],
  durationSec: [120, 180, 300],
  teams: [0, 1],
} as const satisfies Record<keyof RoomSettings, readonly number[]>;

export function defaultSettings(rules: MatchRules): RoomSettings {
  return {
    maxPlayers: MAX_PLAYERS,
    targetScore: rules.targetScore,
    durationSec: Math.round(rules.durationTicks / TICK_RATE),
    teams: 0,
  };
}

/** The server's base rules with the lobby's choices applied. */
export function rulesWithSettings(base: MatchRules, settings: RoomSettings): MatchRules {
  return { ...base, targetScore: settings.targetScore, durationTicks: settings.durationSec * TICK_RATE };
}

/**
 * Validates a host's settings change. Unknown keys and values outside
 * `SETTINGS_OPTIONS` are refused; the player limit cannot drop below the
 * players already in the room.
 */
export function applySettingsPatch(
  current: RoomSettings,
  patch: unknown,
  playersInRoom: number,
): { settings: RoomSettings } | { error: NoticeCode } {
  if (typeof patch !== "object" || patch === null || Array.isArray(patch)) return { error: NoticeCode.InvalidSettings };
  const next = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (!Object.hasOwn(SETTINGS_OPTIONS, key)) return { error: NoticeCode.InvalidSettings };
    const options: readonly number[] = SETTINGS_OPTIONS[key as keyof RoomSettings];
    if (typeof value !== "number" || !options.includes(value)) return { error: NoticeCode.InvalidSettings };
    next[key as keyof RoomSettings] = value;
  }
  if ((next.gameMode ?? GameMode.Slam) !== (current.gameMode ?? GameMode.Slam) && !("targetScore" in patch)) {
    next.targetScore = next.gameMode === GameMode.KO ? 20 : 10;
  }
  if ((next.gameMode ?? GameMode.Slam) !== (current.gameMode ?? GameMode.Slam) && !("durationSec" in patch)) {
    next.durationSec = next.gameMode === GameMode.KO ? 300 : 180;
  }
  if (next.maxPlayers < playersInRoom) return { error: NoticeCode.RoomTooSmall };
  return { settings: next };
}

// ---------------------------------------------------------------------------
// Lobby rules (GDD §6.3–6.4).
// ---------------------------------------------------------------------------

export interface LobbyMember {
  readonly id: string;
  /** Has picked a champion. */
  readonly ready: boolean;
  /** False while the player is in the reconnection window. */
  readonly connected: boolean;
  /** Join order. */
  readonly order: number;
}

/**
 * Why the requester cannot start the match now, or null if it can. Only the
 * host starts; every player, including the host, must be ready and connected.
 * Players waiting to reconnect do not count toward the minimum.
 */
export function startBlocker(members: readonly LobbyMember[], hostId: string, requesterId: string, minPlayers: number): NoticeCode | null {
  if (requesterId !== hostId) return NoticeCode.NotHost;
  const present = members.filter((m) => m.connected);
  if (present.length < minPlayers) return NoticeCode.NotEnoughPlayers;
  if (members.some((m) => !m.connected || !m.ready)) return NoticeCode.NotReady;
  return null;
}

/** Who becomes host when the host leaves: the earliest to join among the rest. */
export function nextHost(members: readonly LobbyMember[], leavingId: string): string {
  const rest = members.filter((m) => m.id !== leavingId);
  rest.sort((a, b) => Number(b.connected) - Number(a.connected) || a.order - b.order);
  return rest[0]?.id ?? "";
}

// ---------------------------------------------------------------------------
// Lobby messages (client → server) and notices (server → client), GDD §9.3.
// ---------------------------------------------------------------------------

export const LobbyMessage = {
  /** `{ characterId: string }`: picking a champion is also confirming it, there is no "ready" button. One champion per player (D49). */
  Character: "character",
  /** `{ on: boolean }`: into the arena to train (through the museum's painting) or back to the museum (D49). */
  Training: "training",
  /** `{ text: string }`: a line of chat for everyone in the room (D49). */
  Chat: "chat",
  /** `{ arenaId: string }`, host only: the map, from the curator's register with the other rules (D49). */
  Map: "map",
  /** Partial `RoomSettings`, host only. */
  Settings: "settings",
  /** No payload, host only. */
  Start: "start",
  /** `{ sessionId: string }`, host only. */
  Kick: "kick",
  /** No payload, host only: back to the lobby from the results. */
  BackToLobby: "lobby",
  /** `{ team: 1 | 2 }`: move to the other team (team matches only). */
  Team: "team",
  /** No payload, host only: random balanced teams. */
  Shuffle: "shuffle",
  /** No payload, host only: a sparring bot joins (training). Removed with `Kick`. */
  AddBot: "addBot",
} as const;

export const NOTICE_MESSAGE = "notice";
/** Server → everyone: a line of chat, `ChatLine`. */
export const CHAT_MESSAGE = "chat";
export const CHAT_MAX_LENGTH = 120;

export interface ChatLine {
  /** Session id of whoever wrote it. */
  from: string;
  text: string;
}

export const NoticeCode = {
  NotHost: "not_host",
  NotInLobby: "not_in_lobby",
  NotEnoughPlayers: "not_enough_players",
  NotReady: "not_ready",
  InvalidSettings: "invalid_settings",
  RoomTooSmall: "room_too_small",
  UnknownCharacter: "unknown_character",
  InvalidMessage: "invalid_message",
  HostChanged: "host_changed",
  NotTeams: "not_teams",
  TeamUnbalanced: "team_unbalanced",
  RoomFull: "room_full",
  ChampionTaken: "champion_taken",
  NoChampion: "no_champion",
  ChatTooFast: "chat_too_fast",
} as const;
export type NoticeCode = (typeof NoticeCode)[keyof typeof NoticeCode];

export interface Notice {
  code: NoticeCode;
  /** Safe, user-facing text (never internals). */
  message: string;
}

export const NOTICE_TEXT: Record<NoticeCode, string> = {
  not_host: "Solo chi ha creato la stanza può farlo.",
  not_in_lobby: "Si può fare solo nella lobby.",
  not_enough_players: "Servono almeno 2 giocatori.",
  not_ready: "C'è ancora qualche fantasma senza campione.",
  invalid_settings: "Impostazione non valida.",
  room_too_small: "Nella stanza ci sono già più giocatori di così.",
  unknown_character: "Campione non disponibile.",
  invalid_message: "Richiesta non valida.",
  host_changed: "Ora sei tu l'host della stanza.",
  not_teams: "Si può fare solo nelle partite a squadre.",
  team_unbalanced: "Le squadre devono restare pari (al massimo un giocatore di differenza).",
  room_full: "La stanza è piena.",
  champion_taken: "Questo campione l'ha già preso un altro giocatore.",
  no_champion: "Prima scegli un campione: tocca una statua.",
  chat_too_fast: "Piano con la chat: aspetta un attimo.",
};

export function notice(code: NoticeCode): Notice {
  return { code, message: NOTICE_TEXT[code] };
}

/** Payload parsers: never trust the wire (GDD §9.4). */
export function parseMap(payload: unknown): string | null {
  return isRecord(payload) && typeof payload.arenaId === "string" && payload.arenaId.length <= 32 ? payload.arenaId : null;
}

export function parseTraining(payload: unknown): boolean | null {
  return isRecord(payload) && typeof payload.on === "boolean" ? payload.on : null;
}

/** A line of chat: no control characters, spaces collapsed, at most `CHAT_MAX_LENGTH`; null when nothing is left. */
export function parseChat(payload: unknown): string | null {
  if (!isRecord(payload) || typeof payload.text !== "string") return null;
  const text = payload.text.replace(/[\p{Cc}\p{Cf}]/gu, " ").replace(/\s+/g, " ").trim().slice(0, CHAT_MAX_LENGTH).trim();
  return text.length > 0 ? text : null;
}

export function parseCharacter(payload: unknown): string | null {
  return isRecord(payload) && typeof payload.characterId === "string" && payload.characterId.length <= 64
    ? payload.characterId
    : null;
}

export function parseTeam(payload: unknown): TeamId | null {
  return isRecord(payload) && isTeam(payload.team) ? payload.team : null;
}

export function parseKick(payload: unknown): string | null {
  return isRecord(payload) && typeof payload.sessionId === "string" && payload.sessionId.length <= 64
    ? payload.sessionId
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
